import { getCurrencySymbol } from './currency-symbol.util';

/**
 * Plan prices from subscription-service (FNF-1281, FNF-1286).
 *
 * FLAT: `amountCents` every month, in advance.
 * PER_ORDER_WITH_FLAT_CAP: billed after the month ends from completed orders,
 * min(orders x perOrderAmountCents, flatAmountCents); `amountCents` is 0.
 */
export type PricingModel = 'FLAT' | 'PER_ORDER_WITH_FLAT_CAP';

export interface PlanPriceLike {
  amountCents: number;
  currency?: string;
  pricingModel?: PricingModel;
  orderLimit?: number | null;
  perOrderAmountCents?: number | null;
  flatAmountCents?: number | null;
}

export interface PlanLike {
  sortOrder?: number;
  currentPrice?: PlanPriceLike | null;
}

/** A price split for display: "$0.50" + "/order" + "max $200.00/mo · up to 400 orders",
 * or "$0.00" + "Free forever" for a free per-order price. */
export interface PriceLabel {
  amount: string;
  suffix: string;
  detail?: string;
}

const isPerOrder = (price?: PlanPriceLike | null) =>
  price?.pricingModel === 'PER_ORDER_WITH_FLAT_CAP';

const money = (cents: number, currency = 'USD') =>
  `${getCurrencySymbol(currency)}${(cents / 100).toFixed(2)}`;

export function priceLabel(price?: PlanPriceLike | null): PriceLabel {
  const currency = price?.currency ?? 'USD';
  if (!price) {
    return { amount: money(0, currency), suffix: '/mon' };
  }
  if (isPerOrder(price)) {
    const perOrder = price.perOrderAmountCents ?? 0;
    const flat = price.flatAmountCents ?? 0;
    if (perOrder === 0 && flat === 0) {
      // Not "Free": the plan's name usually is, and the card shows both (FNF-1286).
      return {
        amount: money(0, currency),
        suffix: '',
        detail: 'Free forever',
      };
    }
    return {
      amount: money(perOrder, currency),
      suffix: '/order',
      detail: `Max ${money(flat, currency)}/mo · flat above ${price.orderLimit ?? 0} orders`,
    };
  }
  return { amount: money(price.amountCents, currency), suffix: '/mon' };
}

/** Whether choosing this price can lead to a charge, so a card is needed first. */
export function isPaidPrice(price?: PlanPriceLike | null): boolean {
  if (!price) return false;
  if (isPerOrder(price)) {
    return (price.perOrderAmountCents ?? 0) > 0 || (price.flatAmountCents ?? 0) > 0;
  }
  return price.amountCents > 0;
}

/**
 * 'upgrade' / 'downgrade' / null, by the same rule subscription-service applies
 * (plan_change_direction): compare the monthly amounts, and fall back to
 * sortOrder when they're equal — which is always the case for per-order prices
 * (amountCents 0). A downgrade is scheduled for the end of the period (FNF-1297).
 */
export function planChangeDirection(
  from: PlanLike | null | undefined,
  to: PlanLike,
): 'upgrade' | 'downgrade' | null {
  const fromAmount = from?.currentPrice?.amountCents;
  const toAmount = to.currentPrice?.amountCents;
  if (fromAmount != null && toAmount != null && fromAmount !== toAmount) {
    return toAmount > fromAmount ? 'upgrade' : 'downgrade';
  }
  const fromOrder = from?.sortOrder ?? 0;
  const toOrder = to.sortOrder ?? 0;
  if (toOrder > fromOrder) return 'upgrade';
  if (toOrder < fromOrder) return 'downgrade';
  return null;
}

/**
 * The end of the billing period running at `nowSeconds` (epoch seconds). The
 * stored period can lag behind until Stripe reports a renewal, so roll it forward
 * by whole months — the same rule subscription-service uses for a downgrade's
 * effective date (plan_changes.period_end_after).
 */
export function periodEndAfter(periodEndSeconds: number, nowSeconds: number): number {
  const end = new Date(periodEndSeconds * 1000);
  while (end.getTime() / 1000 <= nowSeconds) {
    end.setUTCMonth(end.getUTCMonth() + 1);
  }
  return Math.floor(end.getTime() / 1000);
}
